import { EPlayerStatus } from '@lbs/status'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Chip, IconButton } from '@mui/material'
import Decimal from 'decimal.js'
import { MaterialReactTable, MRT_ColumnDef, MRT_ColumnFiltersState, MRT_VisibilityState } from 'material-react-table'
import { MRT_Localization_RU } from 'material-react-table/locales/ru'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'
import { toast } from 'react-toastify'
import { useLocalStorage } from 'react-use'
import routes from 'router/routes'
import PlayersV2, { IPlayerV2 } from '../../api/playersV2'
import { CrudIconBtn, Currency, DialogAddPlayerBalance, FilterBar, LoadMoreRows } from '../../components'
import { ELocalstorageKeys } from '../../constants/localstorageKeys'
import { mrtDateTimeFilter } from '../../services/mrtFilters'
import { useStore } from '../../stores'
import { EDrawerView } from '../../stores/DrawerCRUD.store'
import { moneyFormat } from '../../utils/money'

const fetchSize = 25

const Players: FC = observer(() => {
  const { drawerCRUDStore } = useStore()
  const { ref, inView } = useInView()

  const [columnVisibilityLocalStorage, setColumnVisibilityLocalStorage] = useLocalStorage<MRT_VisibilityState>(
    ELocalstorageKeys.TABLE_PLAYERS_COLUMN_VISIBILITY,
    {}
  )
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    columnVisibilityLocalStorage as MRT_VisibilityState
  )
  const [columnFiltersLocalStorage, setColumnFiltersLocalStorage] = useLocalStorage<MRT_ColumnFiltersState>(
    ELocalstorageKeys.TABLE_PLAYERS_COLUMN_FILTERS,
    []
  )
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    columnFiltersLocalStorage as MRT_ColumnFiltersState
  )
  const [dialogBalance, setDialogBalance] = useState<{
    open: boolean
    playerId: string
    playerLogin: string
  }>({
    open: false,
    playerId: '',
    playerLogin: '',
  })

  const { data, isFetching, isError, isLoading, isFetchingNextPage, fetchNextPage, refetch } = useInfiniteQuery(
    ['players list'],
    async ({ pageParam = 0 }) => {
      return PlayersV2.getPlayers({
        limit: fetchSize,
        offset: pageParam * fetchSize,
      })
    },
    {
      enabled: true,
      getNextPageParam: (_lastGroup, groups) => groups.length,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onError: (err) => toast.error(`${err}`),
    }
  )

  const columns: MRT_ColumnDef<IPlayerV2>[] = [
    {
      accessorKey: 'login',
      header: 'Логин',
    },
    {
      accessorKey: 'status',
      header: 'Статус',
      filterVariant: 'select',
      filterSelectOptions: [
        { text: 'Активен', value: String(EPlayerStatus.ACTIVE) },
        { text: 'Заблокирован', value: String(EPlayerStatus.BLOCKED) },
        { text: 'Удален', value: String(EPlayerStatus.DELETED) },
        { text: 'Не активирован', value: String(EPlayerStatus.NOT_ACTIVATED) },
      ],
      muiFilterTextFieldProps: {
        size: 'small',
      },
      Cell: ({ cell }) => {
        const status = cell.getValue<EPlayerStatus>()

        let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default'
        let label = ''

        switch (status) {
          case EPlayerStatus.ACTIVE:
            color = 'success'
            label = 'Активен'
            break
          case EPlayerStatus.BLOCKED:
            color = 'info'
            label = 'Заблокирован'
            break
          case EPlayerStatus.DELETED:
            color = 'warning'
            label = 'Удален'
            break
          case EPlayerStatus.NOT_ACTIVATED:
            color = 'secondary'
            label = 'Не активирован'
            break
          default:
            label = 'Неизвестно'
        }

        return <Chip variant="outlined" color={color} size="small" label={label} />
      },
    },
    {
      accessorKey: 'availableBalance',
      header: 'Доступно',
      filterFn: 'greaterThanOrEqualTo',
      Cell: ({ row }) => {
        const availableBalance = new Decimal(row.original.availableBalance).toString()

        return (
          <Box
            sx={{
              fontWeight: 'bold',
              color: 'text.secondary',
              fontFamily: 'monospace',
            }}>
            {moneyFormat(availableBalance)}&nbsp;
            <Currency currency={row.original.currency} />
          </Box>
        )
      },
    },
    {
      accessorFn: (row) => {
        const balance = new Decimal(row.balance)
        const availableBalance = new Decimal(row.availableBalance)
        return balance.minus(availableBalance).toString()
      },
      id: 'reservedBalance',
      header: 'В играх',
      filterFn: 'greaterThanOrEqualTo',
      Cell: ({ row, cell }) => {
        const value = cell.getValue<string>() || '0'

        return (
          <Box
            sx={{
              fontWeight: 'bold',
              color: 'text.secondary',
              fontFamily: 'monospace',
            }}>
            {moneyFormat(value)}&nbsp;
            <Currency currency={row.original.currency} />
          </Box>
        )
      },
    },
    {
      accessorKey: 'balance',
      header: 'Общий баланс',
      filterFn: 'greaterThanOrEqualTo',
      Cell: ({ cell, row }) => {
        const balance = cell.getValue<string>() || '0'

        return (
          <Chip
            variant="filled"
            color={!Number(balance) ? 'error' : 'success'}
            size="small"
            label={
              <Box
                sx={{
                  fontWeight: 'bold',
                  fontFamily: 'monospace',
                }}>
                {moneyFormat(balance)}&nbsp;
                <Currency currency={row.original.currency} />
              </Box>
            }
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()

              if (row.original.status !== EPlayerStatus.ACTIVE) {
                toast.error(`Игрок ${row.original.login} не активен`)
                return
              }

              setDialogBalance({
                open: true,
                playerId: row.original.id,
                playerLogin: row.original.login,
              })
            }}
          />
        )
      },
    },
    {
      accessorKey: 'gameRatio',
      header: 'Игровой коэффициент',
      filterFn: 'greaterThanOrEqualTo',
    },
  ]

  useEffect(() => {
    if (!drawerCRUDStore.isOpen) refetch()
  }, [drawerCRUDStore.isOpen])

  useEffect(() => {
    setColumnVisibilityLocalStorage(columnVisibility)
  }, [columnVisibility])

  useEffect(() => {
    setColumnFiltersLocalStorage(columnFilters)
  }, [columnFilters])

  const players = data?.pages.flatMap((item) => item.rows ?? []) || []
  const playersRows = players.length
  const playersTotalRows = data?.pages?.[0]?.count || 0

  useEffect(() => {
    if (inView && playersRows < playersTotalRows) {
      fetchNextPage()
    }
  }, [inView, playersRows, playersTotalRows, fetchNextPage])

  return (
    <>
      <Box>
        <FilterBar>
          <IconButton
            onClick={(e) => {
              e.preventDefault()

              drawerCRUDStore.changeView(EDrawerView.PLAYER_CREATE)
            }}>
            <AddIcon />
          </IconButton>
        </FilterBar>

        <MaterialReactTable
          localization={MRT_Localization_RU}
          enableFullScreenToggle={false}
          enableGlobalFilter={false}
          enableFilterMatchHighlighting={false}
          filterFns={{
            mrtDateTimeFilter,
          }}
          state={{
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isFetching,
            columnVisibility,
            columnFilters,
          }}
          initialState={{
            showColumnFilters: true,
            density: 'comfortable',
          }}
          onColumnVisibilityChange={setColumnVisibility}
          onColumnFiltersChange={setColumnFilters}
          columns={columns}
          data={players}
          enablePagination={false}
          muiTableBodyRowProps={({ row }) => ({
            onClick: (event) => {
              event.preventDefault()
              event.stopPropagation()

              drawerCRUDStore.changeView(EDrawerView.PLAYER_EDIT, row.original.id)
            },
            sx: {
              cursor: 'pointer',
            },
          })}
          renderTopToolbarCustomActions={({ table }) => {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}>
                <Button size="small" variant="outlined" onClick={() => table.resetColumnFilters()}>
                  Сбросить фильтры
                </Button>
              </Box>
            )
          }}
          renderBottomToolbarCustomActions={() => {
            return (
              <LoadMoreRows
                ref={ref}
                rows={playersRows}
                totalRows={playersTotalRows}
                isLoading={isFetchingNextPage}
                onLoad={() => {
                  fetchNextPage()
                }}
              />
            )
          }}
          enableEditing
          renderRowActions={({ row }) => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
              <CrudIconBtn
                color="info"
                tooltip="Финансовая история игрока"
                icon={<AccountBalanceWalletIcon />}
                onClick={() => {
                  const route = routes.playerFinancialHistory.replace(':playerId', row.original.id)
                  window.open(route, '_blank')
                }}
              />
              {/* <CrudIconBtn
                color="info"
                tooltip="Результы игр"
                icon={<ScoreboardIcon />}
                onClick={() => {
                  navigate(`/player/${row.original.id}/game/results`)
                }}
              /> */}
            </Box>
          )}
        />
      </Box>
      {dialogBalance.open && (
        <DialogAddPlayerBalance
          isOpen={dialogBalance.open}
          playerId={dialogBalance.playerId}
          playerLogin={dialogBalance.playerLogin}
          onSave={async () => {
            try {
              await refetch()
            } catch (e) {
              toast.error(`${e}`)
            }
          }}
          onClose={() => {
            setDialogBalance({
              open: false,
              playerId: '',
              playerLogin: '',
            })
          }}
        />
      )}
    </>
  )
})

export default Players
